import React, { ReactElement, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { AlertContainer } from "./AlertContainer";
import { Icon, H5 } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { AlertType } from "./AlertType";

export type AlertStyleType = {
  colour: string;
  closeColour: string;
  icon: keyof typeof IconType;
};

export function Alert({
  type = "default",
  title,
  message,
  className,
  onDismiss,
  isExpandable = false,
  children,
}: {
  type?: AlertType;
  title?: string;
  message: string | ReactNode;
  onDismiss?: () => void;
  isExpandable?: boolean;
  className?: string;
  children?: ReactNode;
}): ReactElement {
  const getMessageStyles = (): AlertStyleType => {
    switch (type) {
      case "information":
        return {
          colour: "text-blue-800",
          closeColour: "text-blue-800",
          icon: "INFORMATION",
        };
      case "success":
        return {
          colour: "text-emerald-800",
          closeColour: "text-emerald-800",
          icon: "CHECK_CIRCLE",
        };
      case "warning":
        return {
          colour: "text-orange-800",
          closeColour: "text-orange-800",
          icon: "CAUTION",
        };
      case "error":
        return {
          colour: "text-red-800",
          closeColour: "text-red-800",
          icon: "ALERT_FILLED",
        };
      case "sellar":
        return {
          colour: "text-[#b557f1]",
          closeColour: "",
          icon: "SELLAR",
        };
      default:
        return {
          colour: "",
          closeColour: "",
          icon: "INFORMATION",
        };
    }
  };
  const { icon, colour, closeColour } = getMessageStyles();
  const [expanded, setExpanded] = useState(false);

  return (
    <AlertContainer type={type} className={className}>
      <div className="flex flex-row justify-between">
        <div className="blue-700 flex flex-row">
          <div className="flex shrink-0 items-start pt-0.5">
            <Icon size={0.8} icon={icon} className={twMerge(colour, "mr-2")} />
          </div>
          <div className="flex flex-col">
            {title && (
              <div className="flex-start flex">
                <H5>{title}</H5>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {isExpandable ? (
                expanded && (
                  <>
                    <span className={"text-black"}>{message}</span>
                    {children}
                  </>
                )
              ) : (
                <>
                  {message && (
                    <span className={"text-base text-zinc-700"}>{message}</span>
                  )}
                  {children}
                </>
              )}
            </div>
          </div>
        </div>
        {onDismiss && !isExpandable && (
          <div
            className="flex-end ml-2 flex cursor-pointer"
            onClick={onDismiss}
          >
            <Icon icon={"CLOSE"} className={closeColour} />
          </div>
        )}

        {isExpandable && (
          <div
            className="flex-end flex shrink-0 cursor-pointer items-start"
            onClick={() => setExpanded((current) => !current)}
          >
            <Icon
              icon={expanded ? "CHEVRON_UP" : "CHEVRON_DOWN"}
              className={closeColour}
            />
          </div>
        )}
      </div>
    </AlertContainer>
  );
}
